import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useDirection } from '../../../contexts/DirectionContext';
import { languageConfigs, LanguageConfig } from '../../../i18n/config';

interface LanguageSwitcherProps {
  openUpward?: boolean;
  menuStyle?: boolean;
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  openUpward = false,
  menuStyle = false,
  className = '',
}) => {
  const { i18n } = useTranslation();
  const { isRTL } = useDirection();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languageConfigs[i18n.language] || languageConfigs.en;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  const getDropdownAlignment = () => {
    if (menuStyle) return isRTL ? 'right-0' : 'left-0';
    return isRTL ? 'left-0' : 'right-0';
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2
          px-3 py-1.5 rounded-md
          ${menuStyle ? 'w-full justify-between' : ''}
          rtl:flex-row-reverse
          bg-surface-secondary hover:bg-surface-tertiary
          text-text-primary
          transition-colors duration-200
          focus-visible
        `}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-xl">{currentLanguage.flag}</span>
        <span className="text-sm font-medium">{currentLanguage.name}</span>
        <Icon
          icon={isOpen ? 'ph:caret-up-thin' : 'ph:caret-down-thin'}
          className={`
            w-4 h-4
            text-text-secondary
            transition-transform duration-200
            ${isOpen ? 'transform rotate-180' : ''}
          `}
        />
      </button>

      {isOpen && (
        <div 
          className={`
            absolute
            ${openUpward ? 'bottom-full mb-1' : 'top-full mt-1'}
            ${getDropdownAlignment()}
            w-48
            bg-background-primary
            border border-border-primary
            rounded-md
            shadow-lg
            z-50
            overflow-hidden
            transition-colors duration-200
          `}
        >
          <div className="py-1">
            {Object.values(languageConfigs).map((language: LanguageConfig) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`
                  w-full px-4 py-2
                  flex items-center gap-3
                  text-sm
                  rtl:flex-row-reverse rtl:text-right
                  ${language.code === currentLanguage.code
                    ? `text-primary-600 dark:text-primary-400 font-medium rtl:border-r-2 ltr:border-l-2 border-primary-400/30`
                    : 'text-text-primary hover:bg-surface-secondary'}
                  transition-colors duration-200
                  focus:outline-none
                `}
              >
                <span className="text-xl flex-shrink-0">{language.flag}</span>
                <span>{language.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher; 